import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('burns-elite-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('burns-elite-language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'fr' : 'en');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Acadian French translations (français acadien)
// Note: Acadian French has unique expressions and vocabulary from the Maritime provinces
const translations = {
  en: {
    nav: {
      about: "ABOUT",
      skills: "SKILLS",
      camps: "CAMPS",
      coaches: "COACHES",
      gallery: "GALLERY",
      contact: "CONTACT",
      merch: "MERCH",
      registerNow: "REGISTER NOW",
      calendar: "CALENDAR",
      parentPortal: "PARENT PORTAL"
    },
    hero: {
      title: "ELEVATE YOUR",
      titleAccent: "GAME",
      subtitle: "Elite baseball training for players ages U9-U15 in Miramichi, New Brunswick",
      services: "PLAYER DEVELOPMENT • CAMPS • CLINICS",
      joinCamp: "JOIN A CAMP",
      viewCalendar: "VIEW CALENDAR"
    },
    founder: {
      sectionTitle: "MEET THE",
      sectionTitleAccent: "FOUNDER",
      yearsLabel: "YEARS",
      learnMore: "LEARN MORE",
      readFullProfile: "READ FULL PROFILE",
      credentials: "CREDENTIALS & ACHIEVEMENTS",
      founderTitle: "Founder & Head Coach",
      playerDev: "Player Development",
      hitting: "Hitting",
      teamStrategy: "Team Strategy",
      mentalGame: "Mental Game",
      pitching: "Pitching",
      fielding: "Fielding",
      baseRunning: "Base Running"
    },
    skills: {
      sectionTitle: "WHAT WE",
      sectionTitleAccent: "DEVELOP",
      subtitle: "Comprehensive training programs designed to elevate every aspect of your game",
      hitting: "HITTING",
      hittingDesc: "Master swing mechanics, timing, and plate discipline. Develop power and consistency at the plate.",
      pitching: "PITCHING",
      pitchingDesc: "Learn proper mechanics, pitch variety, and mental approach. Build arm strength and durability.",
      fielding: "FIELDING",
      fieldingDesc: "Develop defensive fundamentals, footwork, and game awareness. Position-specific training available.",
      gameIQ: "GAME IQ",
      gameIQDesc: "Understand situational baseball, base running, and strategic thinking. Become a complete player."
    },
    camps: {
      sectionTitle: "UPCOMING",
      sectionTitleAccent: "CAMPS",
      subtitle: "Join our elite training camps and take your game to the next level",
      register: "REGISTER",
      viewAll: "VIEW ALL CAMPS",
      spotsLeft: "spots left",
      full: "FULL",
      joinWaitlist: "JOIN WAITLIST"
    },
    coaches: {
      sectionTitle: "OUR",
      sectionTitleAccent: "COACHES",
      subtitle: "Learn from experienced coaches dedicated to developing the next generation of baseball players",
      yearsExperience: "YEARS EXPERIENCE",
      pitchingCoach: "Pitching Coach",
      hittingCoach: "Hitting Coach",
      fieldingCoach: "Fielding Coach",
      assistantCoach: "Assistant Coach",
      headCoach: "Head Coach",
      hittingOutfieldCoach: "Hitting & Outfield Coach",
      infieldCatchingCoach: "Infield & Catching Coach",
      outfieldCoach: "Outfield Coach",
      catchingCoach: "Catching Coach",
      infieldCoach: "Infield Coach",
      youthCoaching: "Youth Coaching",
      armCare: "Arm Care",
      strengthTraining: "Strength Training",
      conditioning: "Conditioning",
      pitchingMechanics: "Pitching Mechanics",
      hittingMechanics: "Hitting Mechanics",
      outfieldDefense: "Outfield Defense",
      infieldDefense: "Infield Defense",
      catching: "Catching",
      gameStrategy: "Game Strategy"
    },
    gallery: {
      sectionTitle: "CAMP",
      sectionTitleAccent: "GALLERY",
      subtitle: "See our athletes in action during training sessions and camps",
      watchVideo: "WATCH CAMP VIDEO",
      moments: "CAMP MOMENTS",
      momentsSubtitle: "See our players in action at Burns Elite Baseball camps"
    },
    video: {
      sectionTitle: "SEE US IN",
      sectionTitleAccent: "ACTION",
      subtitle: "Watch our players develop their skills on the field",
      trainingClip: "TRAINING CLIP"
    },
    testimonials: {
      sectionTitle: "WHAT PARENTS",
      sectionTitleAccent: "SAY",
      subtitle: "Hear from families who have experienced the Burns Elite difference"
    },
    faq: {
      sectionTitle: "FREQUENTLY ASKED",
      sectionTitleAccent: "QUESTIONS",
      subtitle: "Everything you need to know about our programs"
    },
    contact: {
      sectionTitle: "GET IN",
      sectionTitleAccent: "TOUCH",
      subtitle: "Have questions? We'd love to hear from you",
      sendMessage: "SEND US A MESSAGE",
      name: "Name",
      namePlaceholder: "Your Name",
      email: "Email",
      emailPlaceholder: "Email Address",
      phone: "Phone (Optional)",
      phonePlaceholder: "Phone Number",
      message: "Message",
      messagePlaceholder: "Your Message",
      send: "SEND MESSAGE",
      sending: "SENDING...",
      location: "LOCATION",
      emailLabel: "EMAIL",
      followUs: "FOLLOW US"
    },
    newsletter: {
      title: "STAY IN THE GAME",
      subtitle: "Subscribe for camp updates, training tips, and exclusive offers",
      placeholder: "Enter your email",
      subscribe: "SUBSCRIBE",
      subscribing: "SUBSCRIBING..."
    },
    merch: {
      gearUp: "GEAR UP WITH",
      gearUpAccent: "BURNS ELITE",
      description: "Pre-order your official Burns Elite Baseball t-shirt. No obligation required - we're collecting interest to determine final pricing.",
      preorderNow: "PRE-ORDER NOW"
    },
    equipment: {
      title: "WHAT TO BRING",
      subtitle: "Come prepared for an amazing camp experience",
      required: "Required",
      optional: "Optional",
      heading: "WHAT TO",
      headingAccent: "BRING",
      description: "Make sure your player comes prepared for camp. Here's everything they'll need:",
      items: {
        glove: "Baseball Glove",
        gloveNote: "We have loaners available",
        bat: "Baseball Bat",
        batNote: "Optional - bats provided for drills",
        cleats: "Cleats or Athletic Shoes",
        cleatsNote: "No sandals or open-toe shoes",
        water: "Water Bottle",
        waterNote: "Stay hydrated!",
        cap: "Baseball Cap",
        capNote: "Recommended for sun protection",
        sunscreen: "Sunscreen",
        sunscreenNote: "Apply before arriving",
        clothes: "Athletic Clothes",
        clothesNote: "Comfortable, weather-appropriate",
        battingGloves: "Batting Gloves",
        battingGlovesNote: "Optional"
      }
    },
    policies: {
      sectionTitle: "CAMP",
      sectionTitleAccent: "POLICIES",
      weather: "WEATHER POLICY",
      weatherDesc: "Sessions may be modified or rescheduled due to severe weather. Light rain typically doesn't cancel practice. Parents will be notified via email/text of any changes at least 2 hours before scheduled start time.",
      refund: "REFUND POLICY",
      refundDesc: "Full refund: 14+ days before camp. 50% refund: 7-14 days before camp. No refund within 7 days of start date. Credits may be applied to future sessions on a case-by-case basis.",
      safety: "SAFETY & CONDUCT",
      safetyDesc: "All participants must have a signed waiver on file. We expect respectful behavior from all players. Coaches are first-aid certified. Parents must provide emergency contact and medical information."
    },
    footer: {
      tagline: "Developing elite baseball players in Miramichi, New Brunswick since 2020.",
      quickLinks: "Quick Links",
      programs: "Programs",
      connect: "Connect",
      rights: "All rights reserved.",
      home: "Home",
      aboutUs: "About Us",
      campSchedule: "Camp Schedule",
      summerCamps: "Summer Camps",
      privateLessons: "Private Lessons",
      teamTraining: "Team Training"
    },
    tshirt: {
      title: "T-SHIRT",
      titleAccent: "PRE-ORDER",
      subtitle: "Get your official Burns Elite Baseball t-shirt featuring our iconic logo",
      only: "only",
      priceNotice: "T-Shirt Price:",
      priceDescription: "High-quality official Burns Elite Baseball merchandise. If you're ready to represent the team in style, we recommend securing your pre-order now!",
      noObligation: "No Obligation to Purchase:",
      noObligationDesc: "This is a pre-order to gauge interest. Tom will email you to confirm your order and arrange payment details.",
      productName: "BURNS ELITE OFFICIAL TEE",
      feature1: "Premium Dri-FIT moisture-wicking fabric",
      feature2: "Classic black with front logo",
      feature3: "Youth & Adult sizes available",
      feature4: "Perfect for games & practice",
      perShirt: "per shirt",
      participantInfo: "PARTICIPANT INFORMATION",
      participantName: "Participant Name",
      gender: "Gender",
      age: "Age",
      tshirtSize: "T-Shirt Size",
      selectGender: "Select gender...",
      selectSize: "Select size...",
      parentInfo: "PARENT/GUARDIAN INFORMATION",
      parentName: "Parent/Guardian Name",
      phoneNumber: "Phone Number",
      submitPreorder: "SUBMIT PRE-ORDER",
      submitting: "SUBMITTING...",
      preorderNote: "Pre-order now to secure your $40 Burns Elite t-shirt!",
      successTitle: "PRE-ORDER RECEIVED!",
      successMessage: "Thank you for your interest in Burns Elite Baseball merchandise. Tom will contact you to confirm your order.",
      backToHome: "BACK TO HOME",
      male: "Male",
      female: "Female",
      other: "Other"
    },
    registration: {
      title: "CAMP",
      titleAccent: "REGISTRATION",
      subtitle: "Join the elite training experience",
      successTitle: "REGISTRATION COMPLETE!",
      successMessage: "Thank you for registering! Tom will contact you shortly with payment details and camp information.",
      backToHome: "BACK TO HOME",
      selectCamp: "SELECT A CAMP",
      selectCampPlaceholder: "Choose a camp...",
      spotsRemaining: "spots remaining",
      full: "FULL",
      participantInfo: "PARTICIPANT INFORMATION",
      participantName: "Participant Name",
      gender: "Gender",
      age: "Age",
      skillLevel: "Skill Level",
      selectSkillLevel: "Select skill level...",
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",
      parentInfo: "PARENT/GUARDIAN INFORMATION",
      parentName: "Parent/Guardian Name",
      email: "Email",
      phone: "Phone",
      emergencyInfo: "EMERGENCY CONTACT",
      emergencyName: "Emergency Contact Name",
      emergencyPhone: "Emergency Phone",
      medicalInfo: "MEDICAL INFORMATION",
      medicalPlaceholder: "Any allergies, conditions, or medical information we should know about (optional)",
      waiver: "WAIVER & CONSENT",
      readWaiver: "Read Digital Waiver",
      hideWaiver: "Hide Waiver Text",
      waiverText: "I, the undersigned parent/guardian, hereby grant permission for my child to participate in Burns Elite Baseball Academy programs. I understand that participation involves physical activity and inherent risks.\n\nI release Burns Elite Baseball Academy, its coaches, staff, and volunteers from any and all liability for injuries that may occur during participation, except in cases of gross negligence.\n\nI confirm that my child is physically able to participate and I have disclosed any relevant medical conditions. I authorize emergency medical treatment if necessary and cannot be reached.\n\nI consent to the use of photographs and videos taken during camp activities for promotional purposes.\n\nBy checking the box below and entering my name, I acknowledge that I have read, understood, and agree to the terms above.",
      agreeWaiver: "I have read and agree to the waiver terms",
      signerName: "Parent/Guardian Signature (Type Full Name)",
      photoConsentTitle: "I allow Burns Elite Baseball to use general photos of my child on the website and social media",
      photoConsentBody: "Optional. Group shots, action photos from camps and clinics, and similar marketing photos. We will not publish names alongside individual close-up photos. You can withdraw consent at any time by emailing us.",
      submitRegistration: "SUBMIT REGISTRATION",
      submitting: "SUBMITTING...",
      joinWaitlist: "JOIN WAITLIST",
      waitlistInfo: "This camp is currently full. Join the waitlist to be notified if a spot opens up.",
      waitlistSuccess: "WAITLIST JOINED!",
      waitlistSuccessMessage: "You've been added to the waitlist. We'll notify you if a spot becomes available."
    },
    admin: {
      title: "ADMIN DASHBOARD",
      password: "Password",
      login: "LOGIN",
      loggingIn: "LOGGING IN...",
      backToWebsite: "Back to Website"
    },
    common: {
      required: "Required",
      loading: "Loading...",
      error: "Error",
      success: "Success"
    },
    parent: {
      portalTitle: "Parent Portal",
      portalSubtitle: "View your child's progress reports, upcoming camps, and more.",
      firstTime: "First time here?",
      firstTimeBody: "Click \"Forgot password? / First-time setup\" below — we'll email you a secure link to set your password.",
      email: "Email",
      password: "Password",
      passwordHint: "At least 8 characters",
      signIn: "Sign In",
      forgotLink: "Forgot password? / First-time setup",
      backToSite: "← Back to site",
      welcomeBack: "Welcome back!",
      pleaseCaptcha: "Please complete the captcha",
      pwTooShort: "Password must be at least 8 characters",
      forgotTitle: "First-time setup or password reset",
      forgotIntro: "Enter the email you used when registering your child. We'll send you a secure link to set (or reset) your password.",
      sendLink: "Send Reset Link",
      forgotSent: "If an account exists for that email, we've emailed you a secure link.",
      forgotSentHint: "Check your inbox (and Junk folder). New users have 24 hours; password resets expire in 1 hour.",
      backToLogin: "← Back to login",
      resetTitle: "Set a new password",
      newPassword: "New Password",
      confirmPassword: "Confirm Password",
      pwMismatch: "Passwords don't match",
      missingToken: "Missing reset token. Please use the link from your email.",
      pwUpdated: "Password updated. You're signed in.",
      requestNewLink: "Request a new link",
      tokenInvalid: "This page requires a valid reset link from your email.",
      updatePassword: "Update Password",
      welcome: "Welcome back",
      tagline: "Your child's progress, camps, and updates from Burns Elite Baseball.",
      logout: "Logout",
      nextUp: "Next up for",
      starts_today: "Starts today!",
      starts_tomorrow: "Starts tomorrow",
      startsInDays: "Starts in {days} days",
      weather: "Miramichi Weather",
      weatherSubtitle: "Today + next 3 days · all temperatures in °C",
      today: "Today",
      yourPlayers: "Your Players",
      noRegistrations: "We couldn't find any registrations on file for {email}.",
      upcoming: "Upcoming",
      pastCamps: "Past Camps",
      progressReports: "Progress Reports",
      registerCTA: "Register for an Upcoming Camp",
      noAvailableCamps: "You're already registered for every active camp. Check back soon for the next session.",
      registerArrow: "Register →",
      rain: "rain",
      loadFail: "Failed to load some dashboard data",
      signedOut: "Signed out",
      sectionOverall: "Overall Assessment",
      sectionSkills: "Skills Progress",
      sectionImprove: "Areas for Improvement",
      sectionComments: "Coach's Comments",
      campPhotos: "Camp Photos",
      photosHint: "Tap any photo to share or save using your device's built-in share menu. Only photos from camps your child attended are shown.",
      adminPreviewBanner: "Admin Preview — you're viewing the parent portal as the test parent.",
      backToAdmin: "Back to Admin",
      contactTitle: "Contact the Coaches",
      contactSubtitle: "Send Tom and the coaching staff a question, request, or comment. They'll reply directly to your registered email.",
      contactCategory: "Category",
      catGeneral: "General",
      catCoaches: "Coaches / Training",
      catScheduling: "Scheduling",
      catBilling: "Billing / Payment",
      catOther: "Other",
      contactSubject: "Subject (optional)",
      contactMessage: "Message",
      contactMessagePlaceholder: "Type your message here…",
      contactSend: "Send Message",
      contactSent: "Message sent — the coaches will reply by email.",
      contactHint: "Goes to burnselitebaseball@gmail.com. They'll see your registered email so they can reply directly.",
      surveyTitle: "Camp Experience Survey",
      surveySubtitle: "Help us improve. Five quick questions, two minutes — your feedback shapes future camps.",
      surveyScale: "1 = strongly disagree · 5 = strongly agree",
      surveyQ1: "Overall, our family had a great experience.",
      surveyQ2: "The coaches were knowledgeable, supportive, and engaging.",
      surveyQ3: "Communication before, during, and after camp was clear and timely.",
      surveyQ4: "I would recommend Burns Elite Baseball to other families.",
      surveyFavorite: "What did your child enjoy most?",
      surveyImproved: "Which skill improved the most?",
      surveySuggestions: "What could we do better?",
      surveyOther: "Anything else?",
      surveySubmit: "Submit Survey",
      surveyAllRatings: "Please answer all four rating questions",
      surveyThanks: "Thank you — your feedback was recorded.",
      surveyThanksHint: "Tom reads every response. If you also entered comments, you may hear back personally.",
      reviewTitle: "Write a Review",
      reviewSubtitle: "Share your experience publicly. Reviews appear on burnselitebaseball.com after Tom approves them.",
      reviewRatingLabel: "Your rating",
      reviewText: "Your review",
      reviewTextPlaceholder: "What made Burns Elite Baseball special for your family?",
      reviewName: "Display name",
      reviewNamePlaceholder: "How you'd like to be credited",
      reviewRole: "Role",
      reviewRolePlaceholder: "e.g. Parent of U12 player",
      reviewSubmit: "Submit Review",
      reviewPickStars: "Please pick a star rating",
      reviewWriteText: "Please write a short review",
      reviewThanks: "Thank you for your review!",
      reviewThanksHint: "Tom will see it and decide whether to feature it on the website. You'll see your review live on the site once it's approved.",
      reviewHint: "Reviews are moderated. Nothing is published without Tom's approval.",
      pickCampFirst: "Please pick a camp first",
      pickCampPlaceholder: "Pick a camp",
      noCampsToReview: "No camps on file for your account yet.",
      surveyCampLabel: "Which camp is this survey about?",
      reviewCampLabel: "Which camp are you reviewing?",
      inclementBannerTitle: "Weather alert",
      inclementInMiramichi: "in Miramichi right now",
      inclementBannerBody: "If your child has a camp today, watch your email — we'll send updates if any sessions need to shift.",
      photoSelectMultiple: "Download - select multiple",
      photoCancelSelect: "Cancel",
      photoSelectHint: "Tap photos to select them, then click \"Download\" to grab them all as a ZIP.",
      photoDownloadSelected: "Download {n}",
      photoZipDone: "Photos downloaded",
      photoZipFail: "Download failed — try selecting fewer photos.",
      privacyTitle: "Privacy & Photo Sharing",
      privacySubtitle: "Camp photos are general shots (drills, group action, scrimmages) that may include your child along with other players.",
      consentLabel: "Allow Burns Elite to publish camp photos that may include my child",
      consentBody: "Photos used for the website and social media. Names won't be published alongside individual close-ups, and your child can be cropped out of a specific photo on request. You can change this any time.",
      consentMixed: "Your registrations currently have different consent settings. Saving will apply this choice to all of them.",
      consentScope: "Applies to all {n} of your registrations on file.",
      consentSavedYes: "Photo sharing consent updated — thanks!",
      consentSavedNo: "Photo sharing has been turned off.",
      consentSaveFail: "Couldn't save your preference. Please try again.",
      reportModalTitle: "Player Progress Report",
      reportSent: "Report sent",
      print: "Print",
      viewReport: "View →",
      contactHistoryTitle: "Messages you've sent",
      editDetails: "Edit details",
      cancel: "Cancel",
      save: "Save",
      detailsParticipant: "Participant",
      detailsContact: "Parent contact",
      detailsWaivers: "Signed waivers",
      detailsName: "Name",
      detailsAge: "Age",
      detailsGender: "Gender",
      detailsSkill: "Skill level",
      detailsLanguage: "Language",
      detailsMedical: "Allergies / medical info",
      detailsParentName: "Parent / guardian name",
      detailsParentPhone: "Phone",
      detailsEmergencyName: "Emergency contact name",
      detailsEmergencyPhone: "Emergency phone",
      detailsSaved: "Updated. Changes apply to all of your registrations for this player.",
      detailsFail: "Couldn't save. Please try again.",
      waiverSignedOn: "Signed on {date} by {name}",
      waiverNotSigned: "Not signed",
      waiverView: "View",
      waiverNote: "Waivers shown reflect what was on file when you signed. Contact us if anything looks wrong.",
      waiverModalTitle: "Waiver & Consent",
      waiverSignedShort: "signed {date} by {name}",
      surveyLockedBanner: "Submitted on {date}. Surveys can't be changed once submitted.",
      surveyPendingNudge: "You haven't taken a survey for {n} of your camp{plural} yet. Help Tom by sharing how things went.",
      reviewLockedBanner: "Submitted on {date}. Reviews can't be changed once submitted.",
      reviewApprovedBadge: "approved & live on website",
      reviewPendingNudge: "You haven't reviewed {n} of your camp{plural} yet. Tom would love your feedback."
    }
  },
  fr: {
    // Acadian French (Français Acadien) - Maritime dialect
    nav: {
      about: "À PROPOS",
      skills: "HABILETÉS",
      camps: "CAMPS",
      coaches: "ENTRAÎNEURS",
      gallery: "GALERIE",
      contact: "CONTACT",
      merch: "BOUTIQUE",
      registerNow: "S'INSCRIRE",
      calendar: "CALENDRIER",
      parentPortal: "PORTAIL PARENTS"
    },
    hero: {
      title: "ÉLÈVE TON",
      titleAccent: "JEU",
      subtitle: "Entraînement de baseball élite pour les joueurs U9-U15 à Miramichi, Nouveau-Brunswick",
      services: "DÉVELOPPEMENT • CAMPS • CLINIQUES",
      joinCamp: "JOINDRE UN CAMP",
      viewCalendar: "VOIR LE CALENDRIER"
    },
    founder: {
      sectionTitle: "RENCONTRE LE",
      sectionTitleAccent: "FONDATEUR",
      yearsLabel: "ANNÉES",
      learnMore: "EN SAVOIR PLUS",
      readFullProfile: "LIRE LE PROFIL COMPLET",
      credentials: "DIPLÔMES ET RÉALISATIONS",
      founderTitle: "Fondateur et Entraîneur-Chef",
      playerDev: "Développement des Joueurs",
      hitting: "Frappe",
      teamStrategy: "Stratégie d'Équipe",
      mentalGame: "Jeu Mental",
      pitching: "Lancer",
      fielding: "Défensive",
      baseRunning: "Course de But"
    },
    skills: {
      sectionTitle: "CE QU'ON",
      sectionTitleAccent: "DÉVELOPPE",
      subtitle: "Des programmes d'entraînement complets conçus pour améliorer tous les aspects de ton jeu",
      hitting: "FRAPPE",
      hittingDesc: "Maîtrise les mécaniques de l'élan, le timing et la discipline au bâton. Développe ta puissance et ta régularité.",
      pitching: "LANCER",
      pitchingDesc: "Apprends les bonnes mécaniques, la variété des lancers et l'approche mentale. Développe la force et l'endurance du bras.",
      fielding: "DÉFENSIVE",
      fieldingDesc: "Développe les fondamentaux défensifs, le jeu de pieds et la conscience du jeu. Entraînement spécifique par position disponible.",
      gameIQ: "QI BASEBALL",
      gameIQDesc: "Comprends le baseball situationnel, la course de but et la réflexion stratégique. Deviens un joueur complet."
    },
    camps: {
      sectionTitle: "CAMPS À",
      sectionTitleAccent: "VENIR",
      subtitle: "Joins-toi à nos camps d'entraînement élite et amène ton jeu au prochain niveau",
      register: "S'INSCRIRE",
      viewAll: "VOIR TOUS LES CAMPS",
      spotsLeft: "places restantes",
      full: "COMPLET",
      joinWaitlist: "LISTE D'ATTENTE"
    },
    coaches: {
      sectionTitle: "NOS",
      sectionTitleAccent: "ENTRAÎNEURS",
      subtitle: "Apprends des entraîneurs expérimentés dédiés au développement de la prochaine génération de joueurs de baseball",
      yearsExperience: "ANNÉES D'EXPÉRIENCE",
      pitchingCoach: "Entraîneur de Lancer",
      hittingCoach: "Entraîneur de Frappe",
      fieldingCoach: "Entraîneur de Défensive",
      assistantCoach: "Entraîneur Adjoint",
      headCoach: "Entraîneur-Chef",
      hittingOutfieldCoach: "Entraîneur de Frappe et Champ Extérieur",
      infieldCatchingCoach: "Entraîneur d'Avant-Champ et Receveur",
      outfieldCoach: "Entraîneur de Champ Extérieur",
      catchingCoach: "Entraîneur de Receveur",
      infieldCoach: "Entraîneur d'Avant-Champ",
      youthCoaching: "Entraînement Jeunesse",
      armCare: "Soins du Bras",
      strengthTraining: "Musculation",
      conditioning: "Conditionnement",
      pitchingMechanics: "Mécaniques de Lancer",
      hittingMechanics: "Mécaniques de Frappe",
      outfieldDefense: "Défensive Champ Extérieur",
      infieldDefense: "Défensive Avant-Champ",
      catching: "Attrapé",
      gameStrategy: "Stratégie de Jeu"
    },
    gallery: {
      sectionTitle: "GALERIE DU",
      sectionTitleAccent: "CAMP",
      subtitle: "Vois nos athlètes en action durant les sessions d'entraînement et les camps",
      watchVideo: "REGARDER LA VIDÉO",
      moments: "MOMENTS AU CAMP",
      momentsSubtitle: "Vois nos joueurs en action aux camps Burns Elite Baseball"
    },
    video: {
      sectionTitle: "REGARDE-NOUS EN",
      sectionTitleAccent: "ACTION",
      subtitle: "Regarde nos joueurs développer leurs habiletés sur le terrain",
      trainingClip: "VIDÉO D'ENTRAÎNEMENT"
    },
    testimonials: {
      sectionTitle: "CE QUE LES PARENTS",
      sectionTitleAccent: "DISENT",
      subtitle: "Écoute les familles qui ont vécu l'expérience Burns Elite"
    },
    faq: {
      sectionTitle: "QUESTIONS",
      sectionTitleAccent: "FRÉQUENTES",
      subtitle: "Tout ce que tu dois savoir sur nos programmes"
    },
    contact: {
      sectionTitle: "CONTACTE-",
      sectionTitleAccent: "NOUS",
      subtitle: "Des questions? On serait ben contents d'avoir de tes nouvelles",
      sendMessage: "ENVOIE-NOUS UN MESSAGE",
      name: "Nom",
      namePlaceholder: "Ton Nom",
      email: "Courriel",
      emailPlaceholder: "Adresse Courriel",
      phone: "Téléphone (Optionnel)",
      phonePlaceholder: "Numéro de Téléphone",
      message: "Message",
      messagePlaceholder: "Ton Message",
      send: "ENVOYER",
      sending: "ENVOI EN COURS...",
      location: "EMPLACEMENT",
      emailLabel: "COURRIEL",
      followUs: "SUIS-NOUS"
    },
    newsletter: {
      title: "RESTE DANS LE COUP",
      subtitle: "Abonne-toi pour les mises à jour des camps, conseils d'entraînement et offres exclusives",
      placeholder: "Entre ton courriel",
      subscribe: "S'ABONNER",
      subscribing: "ABONNEMENT..."
    },
    merch: {
      gearUp: "ÉQUIPE-TOI AVEC",
      gearUpAccent: "BURNS ELITE",
      description: "Pré-commande ton chandail officiel Burns Elite Baseball. Aucune obligation - on collecte l'intérêt pour déterminer le prix final.",
      preorderNow: "PRÉ-COMMANDER"
    },
    equipment: {
      title: "QUOI APPORTER",
      subtitle: "Arrive préparé pour une expérience de camp incroyable",
      required: "Requis",
      optional: "Optionnel",
      heading: "QUOI",
      headingAccent: "APPORTER",
      description: "Assure-toi que ton joueur arrive préparé pour le camp. Voici tout ce dont il aura besoin:",
      items: {
        glove: "Gant de Baseball",
        gloveNote: "On a des gants à prêter",
        bat: "Bâton de Baseball",
        batNote: "Optionnel - des bâtons sont fournis pour les exercices",
        cleats: "Crampons ou Chaussures de Sport",
        cleatsNote: "Pas de sandales ou chaussures ouvertes",
        water: "Bouteille d'Eau",
        waterNote: "Reste hydraté!",
        cap: "Casquette de Baseball",
        capNote: "Recommandé pour la protection solaire",
        sunscreen: "Crème Solaire",
        sunscreenNote: "Appliquer avant d'arriver",
        clothes: "Vêtements de Sport",
        clothesNote: "Confortables, appropriés à la météo",
        battingGloves: "Gants de Frappe",
        battingGlovesNote: "Optionnel"
      }
    },
    policies: {
      sectionTitle: "POLITIQUES DU",
      sectionTitleAccent: "CAMP",
      weather: "POLITIQUE MÉTÉO",
      weatherDesc: "Les sessions peuvent être modifiées ou reportées en cas de mauvais temps. La pluie légère n'annule généralement pas la pratique. Les parents seront avisés par courriel/texto de tout changement au moins 2 heures avant l'heure prévue.",
      refund: "POLITIQUE DE REMBOURSEMENT",
      refundDesc: "Remboursement complet: 14+ jours avant le camp. Remboursement 50%: 7-14 jours avant. Aucun remboursement dans les 7 jours précédant le début. Des crédits peuvent être appliqués aux sessions futures au cas par cas.",
      safety: "SÉCURITÉ ET CONDUITE",
      safetyDesc: "Tous les participants doivent avoir une décharge signée au dossier. On s'attend à un comportement respectueux de tous les joueurs. Les entraîneurs sont certifiés en premiers soins. Les parents doivent fournir les coordonnées d'urgence et les informations médicales."
    },
    footer: {
      tagline: "Développement de joueurs de baseball élite à Miramichi, Nouveau-Brunswick depuis 2020.",
      quickLinks: "Liens Rapides",
      programs: "Programmes",
      connect: "Connexion",
      rights: "Tous droits réservés.",
      home: "Accueil",
      aboutUs: "À Propos",
      campSchedule: "Horaire des Camps",
      summerCamps: "Camps d'Été",
      privateLessons: "Leçons Privées",
      teamTraining: "Entraînement d'Équipe"
    },
    tshirt: {
      title: "CHANDAIL",
      titleAccent: "PRÉ-COMMANDE",
      subtitle: "Procure-toi ton chandail officiel Burns Elite Baseball avec notre logo iconique",
      only: "seulement",
      priceNotice: "Prix du Chandail:",
      priceDescription: "Marchandise officielle de haute qualité Burns Elite Baseball. Si t'es prêt à représenter l'équipe avec style, on te recommande de réserver ta pré-commande asteure!",
      noObligation: "Aucune Obligation d'Achat:",
      noObligationDesc: "C'est une pré-commande pour mesurer l'intérêt. Tom va t'envoyer un courriel pour confirmer ta commande et arranger les détails du paiement.",
      productName: "CHANDAIL OFFICIEL BURNS ELITE",
      feature1: "Tissu Dri-FIT premium anti-humidité",
      feature2: "Noir classique avec logo en avant",
      feature3: "Grandeurs jeunesse et adulte disponibles",
      feature4: "Parfait pour les games et la pratique",
      perShirt: "par chandail",
      participantInfo: "INFORMATION DU PARTICIPANT",
      participantName: "Nom du Participant",
      gender: "Genre",
      age: "Âge",
      tshirtSize: "Grandeur du Chandail",
      selectGender: "Choisir le genre...",
      selectSize: "Choisir la grandeur...",
      parentInfo: "INFORMATION DU PARENT/TUTEUR",
      parentName: "Nom du Parent/Tuteur",
      phoneNumber: "Numéro de Téléphone",
      submitPreorder: "SOUMETTRE LA PRÉ-COMMANDE",
      submitting: "SOUMISSION...",
      preorderNote: "Pré-commande asteure pour ton chandail Burns Elite à 40$!",
      successTitle: "PRÉ-COMMANDE REÇUE!",
      successMessage: "Merci pour ton intérêt dans la marchandise Burns Elite Baseball. Tom va te contacter pour confirmer ta commande.",
      backToHome: "RETOUR À L'ACCUEIL",
      male: "Masculin",
      female: "Féminin",
      other: "Autre"
    },
    registration: {
      title: "INSCRIPTION AU",
      titleAccent: "CAMP",
      subtitle: "Joins-toi à l'expérience d'entraînement élite",
      successTitle: "INSCRIPTION COMPLÈTE!",
      successMessage: "Merci pour ton inscription! Tom va te contacter bientôt avec les détails du paiement et les informations du camp.",
      backToHome: "RETOUR À L'ACCUEIL",
      selectCamp: "CHOISIR UN CAMP",
      selectCampPlaceholder: "Choisir un camp...",
      spotsRemaining: "places restantes",
      full: "COMPLET",
      participantInfo: "INFORMATION DU PARTICIPANT",
      participantName: "Nom du Participant",
      gender: "Genre",
      age: "Âge",
      skillLevel: "Niveau d'Habileté",
      selectSkillLevel: "Choisir le niveau...",
      beginner: "Débutant",
      intermediate: "Intermédiaire",
      advanced: "Avancé",
      parentInfo: "INFORMATION DU PARENT/TUTEUR",
      parentName: "Nom du Parent/Tuteur",
      email: "Courriel",
      phone: "Téléphone",
      emergencyInfo: "CONTACT D'URGENCE",
      emergencyName: "Nom du Contact d'Urgence",
      emergencyPhone: "Téléphone d'Urgence",
      medicalInfo: "INFORMATION MÉDICALE",
      medicalPlaceholder: "Allergies, conditions ou informations médicales qu'on devrait savoir (optionnel)",
      waiver: "DÉCHARGE & CONSENTEMENT",
      readWaiver: "Lire la Décharge Numérique",
      hideWaiver: "Cacher le Texte de la Décharge",
      waiverText: "Je, le soussigné parent/tuteur, accorde par la présente la permission à mon enfant de participer aux programmes de Burns Elite Baseball Academy. Je comprends que la participation implique des activités physiques et des risques inhérents.\n\nJe dégage Burns Elite Baseball Academy, ses entraîneurs, son personnel et ses bénévoles de toute responsabilité pour les blessures qui pourraient survenir durant la participation, sauf en cas de négligence grave.\n\nJe confirme que mon enfant est physiquement apte à participer et j'ai divulgué toute condition médicale pertinente. J'autorise le traitement médical d'urgence si nécessaire et qu'on ne peut pas me rejoindre.\n\nJe consens à l'utilisation de photographies et vidéos prises durant les activités du camp à des fins promotionnelles.\n\nEn cochant la case ci-dessous et en entrant mon nom, je reconnais avoir lu, compris et accepté les termes ci-dessus.",
      agreeWaiver: "J'ai lu et j'accepte les termes de la décharge",
      signerName: "Signature du Parent/Tuteur (Écrire le Nom Complet)",
      photoConsentTitle: "J'autorise Burns Elite Baseball à utiliser des photos générales de mon enfant sur le site web et les réseaux sociaux",
      photoConsentBody: "Optionnel. Photos de groupe, photos d'action des camps et cliniques, et photos marketing similaires. On ne publiera pas de noms avec des gros plans individuels. Tu peux retirer ton consentement n'importe quand en nous écrivant.",
      submitRegistration: "SOUMETTRE L'INSCRIPTION",
      submitting: "SOUMISSION...",
      joinWaitlist: "JOINDRE LA LISTE D'ATTENTE",
      waitlistInfo: "Ce camp est présentement complet. Joins la liste d'attente pour être avisé si une place se libère.",
      waitlistSuccess: "LISTE D'ATTENTE JOINTE!",
      waitlistSuccessMessage: "T'as été ajouté à la liste d'attente. On va t'aviser si une place devient disponible."
    },
    admin: {
      title: "TABLEAU DE BORD ADMIN",
      password: "Mot de Passe",
      login: "CONNEXION",
      loggingIn: "CONNEXION...",
      backToWebsite: "Retour au Site"
    },
    common: {
      required: "Requis",
      loading: "Chargement...",
      error: "Erreur",
      success: "Succès"
    },
    parent: {
      portalTitle: "Portail Parents",
      portalSubtitle: "Consulte les rapports de progrès de ton enfant, les camps à venir, et plus encore.",
      firstTime: "Première visite?",
      firstTimeBody: "Clique sur « Mot de passe oublié? / Première configuration » plus bas — on va t'envoyer un courriel avec un lien sécurisé pour configurer ton mot de passe.",
      email: "Courriel",
      password: "Mot de passe",
      passwordHint: "Au moins 8 caractères",
      signIn: "Se Connecter",
      forgotLink: "Mot de passe oublié? / Première configuration",
      backToSite: "← Retour au site",
      welcomeBack: "Bon retour!",
      pleaseCaptcha: "Complète le captcha s'il te plaît",
      pwTooShort: "Le mot de passe doit avoir au moins 8 caractères",
      forgotTitle: "Première configuration ou réinitialisation",
      forgotIntro: "Entre le courriel que t'as utilisé pour inscrire ton enfant. On va t'envoyer un lien sécurisé pour configurer (ou réinitialiser) ton mot de passe.",
      sendLink: "Envoyer le Lien",
      forgotSent: "Si un compte existe pour ce courriel, on t'a envoyé un lien sécurisé.",
      forgotSentHint: "Vérifie ta boîte de réception (et le dossier Junk). Les nouveaux usagers ont 24 heures; les réinitialisations expirent dans 1 heure.",
      backToLogin: "← Retour à la connexion",
      resetTitle: "Configure un nouveau mot de passe",
      newPassword: "Nouveau Mot de Passe",
      confirmPassword: "Confirmer le Mot de Passe",
      pwMismatch: "Les mots de passe ne correspondent pas",
      missingToken: "Lien manquant. Utilise le lien envoyé par courriel.",
      pwUpdated: "Mot de passe mis à jour. T'es connecté.",
      requestNewLink: "Demander un nouveau lien",
      tokenInvalid: "Cette page demande un lien de réinitialisation valide envoyé par courriel.",
      updatePassword: "Mettre à Jour",
      welcome: "Bon retour",
      tagline: "Le progrès de ton enfant, ses camps, et les nouvelles de Burns Elite Baseball.",
      logout: "Déconnexion",
      nextUp: "Prochain camp pour",
      starts_today: "Commence aujourd'hui!",
      starts_tomorrow: "Commence demain",
      startsInDays: "Commence dans {days} jours",
      weather: "Météo Miramichi",
      weatherSubtitle: "Aujourd'hui + les 3 prochains jours · toutes les températures en °C",
      today: "Aujourd'hui",
      yourPlayers: "Tes Joueurs",
      noRegistrations: "Aucune inscription trouvée pour {email}.",
      upcoming: "À venir",
      pastCamps: "Camps Passés",
      progressReports: "Rapports de Progrès",
      registerCTA: "Inscris-toi à un camp",
      noAvailableCamps: "T'es déjà inscrit à tous les camps actifs. Reviens bientôt pour la prochaine session.",
      registerArrow: "S'inscrire →",
      rain: "pluie",
      loadFail: "Échec du chargement de certaines données",
      signedOut: "Déconnecté",
      sectionOverall: "Évaluation Globale",
      sectionSkills: "Progrès des Habiletés",
      sectionImprove: "Points à Améliorer",
      sectionComments: "Commentaires de l'Entraîneur",
      campPhotos: "Photos du Camp",
      photosHint: "Touche une photo pour la partager ou la sauvegarder avec le menu de ton appareil. Seules les photos des camps de ton enfant sont affichées.",
      adminPreviewBanner: "Aperçu Admin — t'es en train de voir le portail parents comme le parent de test.",
      backToAdmin: "Retour à l'Admin",
      contactTitle: "Contacte les Entraîneurs",
      contactSubtitle: "Envoie une question, une demande ou un commentaire à Tom et l'équipe. Ils vont te répondre directement à ton courriel inscrit.",
      contactCategory: "Catégorie",
      catGeneral: "Général",
      catCoaches: "Entraîneurs / Entraînement",
      catScheduling: "Horaire",
      catBilling: "Facturation / Paiement",
      catOther: "Autre",
      contactSubject: "Sujet (optionnel)",
      contactMessage: "Message",
      contactMessagePlaceholder: "Écris ton message ici…",
      contactSend: "Envoyer",
      contactSent: "Message envoyé — les entraîneurs vont te répondre par courriel.",
      contactHint: "Envoyé à burnselitebaseball@gmail.com. Ils vont voir ton courriel inscrit pour pouvoir te répondre.",
      surveyTitle: "Sondage sur l'Expérience du Camp",
      surveySubtitle: "Aide-nous à nous améliorer. Cinq questions rapides, deux minutes — tes commentaires façonnent les camps futurs.",
      surveyScale: "1 = pas du tout d'accord · 5 = totalement d'accord",
      surveyQ1: "Notre famille a eu une excellente expérience en général.",
      surveyQ2: "Les entraîneurs étaient compétents, supportants et motivants.",
      surveyQ3: "La communication avant, pendant et après le camp était claire et opportune.",
      surveyQ4: "Je recommanderais Burns Elite Baseball à d'autres familles.",
      surveyFavorite: "Qu'est-ce que ton enfant a le plus aimé?",
      surveyImproved: "Quelle habileté s'est le plus améliorée?",
      surveySuggestions: "Qu'est-ce qu'on pourrait améliorer?",
      surveyOther: "Autre chose?",
      surveySubmit: "Soumettre le Sondage",
      surveyAllRatings: "S'il te plaît, réponds aux quatre questions de notation",
      surveyThanks: "Merci — tes commentaires ont été enregistrés.",
      surveyThanksHint: "Tom lit chaque réponse. Si t'as laissé des commentaires, tu pourrais avoir une réponse personnelle.",
      reviewTitle: "Écris un Avis",
      reviewSubtitle: "Partage ton expérience publiquement. Les avis apparaissent sur burnselitebaseball.com après l'approbation de Tom.",
      reviewRatingLabel: "Ta note",
      reviewText: "Ton avis",
      reviewTextPlaceholder: "Qu'est-ce qui a rendu Burns Elite Baseball spécial pour ta famille?",
      reviewName: "Nom à afficher",
      reviewNamePlaceholder: "Comment tu veux être crédité",
      reviewRole: "Rôle",
      reviewRolePlaceholder: "ex: Parent d'un joueur U12",
      reviewSubmit: "Soumettre l'Avis",
      reviewPickStars: "Choisis une note en étoiles",
      reviewWriteText: "Écris un court avis",
      reviewThanks: "Merci pour ton avis!",
      reviewThanksHint: "Tom va le voir et décider de le mettre sur le site web. Tu vas voir ton avis en ligne une fois approuvé.",
      reviewHint: "Les avis sont modérés. Rien n'est publié sans l'approbation de Tom.",
      pickCampFirst: "Choisis un camp en premier",
      pickCampPlaceholder: "Choisis un camp",
      noCampsToReview: "Aucun camp inscrit à ton compte pour l'instant.",
      surveyCampLabel: "Pour quel camp est ce sondage?",
      reviewCampLabel: "Quel camp évalues-tu?",
      inclementBannerTitle: "Alerte météo",
      inclementInMiramichi: "à Miramichi en ce moment",
      inclementBannerBody: "Si ton enfant a un camp aujourd'hui, surveille tes courriels — on va t'aviser si des sessions doivent changer.",
      photoSelectMultiple: "Télécharger - sélectionner plusieurs",
      photoCancelSelect: "Annuler",
      photoSelectHint: "Touche les photos pour les sélectionner, puis clique « Télécharger » pour les récupérer toutes en ZIP.",
      photoDownloadSelected: "Télécharger {n}",
      photoZipDone: "Photos téléchargées",
      photoZipFail: "Échec du téléchargement — essaie d'en sélectionner moins.",
      privacyTitle: "Confidentialité et Partage de Photos",
      privacySubtitle: "Les photos du camp sont des photos générales (exercices, action de groupe, parties) qui peuvent inclure ton enfant avec d'autres joueurs.",
      consentLabel: "Autoriser Burns Elite à publier des photos de camp qui peuvent inclure mon enfant",
      consentBody: "Photos utilisées pour le site web et les réseaux sociaux. On ne publiera pas de noms avec les gros plans individuels, et ton enfant peut être enlevé d'une photo spécifique sur demande. Tu peux changer ça n'importe quand.",
      consentMixed: "Tes inscriptions ont actuellement des préférences différentes. Sauvegarder appliquera ce choix à toutes.",
      consentScope: "S'applique à toutes tes {n} inscriptions au dossier.",
      consentSavedYes: "Consentement mis à jour — merci!",
      consentSavedNo: "Le partage de photos a été désactivé.",
      consentSaveFail: "Impossible de sauvegarder ta préférence. Réessaie.",
      reportModalTitle: "Rapport de Progrès du Joueur",
      reportSent: "Rapport envoyé",
      print: "Imprimer",
      viewReport: "Voir →",
      contactHistoryTitle: "Messages que t'as envoyés",
      editDetails: "Modifier",
      cancel: "Annuler",
      save: "Sauvegarder",
      detailsParticipant: "Joueur",
      detailsContact: "Coordonnées du parent",
      detailsWaivers: "Décharges signées",
      detailsName: "Nom",
      detailsAge: "Âge",
      detailsGender: "Genre",
      detailsSkill: "Niveau",
      detailsLanguage: "Langue",
      detailsMedical: "Allergies / infos médicales",
      detailsParentName: "Nom du parent / tuteur",
      detailsParentPhone: "Téléphone",
      detailsEmergencyName: "Nom du contact d'urgence",
      detailsEmergencyPhone: "Téléphone d'urgence",
      detailsSaved: "Mis à jour. Les changements s'appliquent à toutes les inscriptions de ce joueur.",
      detailsFail: "Échec de la sauvegarde. Réessaie.",
      waiverSignedOn: "Signé le {date} par {name}",
      waiverNotSigned: "Pas signé",
      waiverView: "Voir",
      waiverNote: "Les décharges affichées reflètent ce qui était au dossier lors de la signature. Contacte-nous si quelque chose semble erroné.",
      waiverModalTitle: "Décharge et Consentement",
      waiverSignedShort: "signé le {date} par {name}",
      surveyLockedBanner: "Soumis le {date}. Les sondages ne peuvent pas être modifiés une fois soumis.",
      surveyPendingNudge: "T'as pas encore fait de sondage pour {n} de tes camp{plural}. Aide Tom en partageant comment ça s'est passé.",
      reviewLockedBanner: "Soumis le {date}. Les avis ne peuvent pas être modifiés une fois soumis.",
      reviewApprovedBadge: "approuvé et en ligne",
      reviewPendingNudge: "T'as pas encore évalué {n} de tes camp{plural}. Tom aimerait avoir tes commentaires."
    }
  }
};

export default LanguageContext;
